import React, { useEffect, useState } from 'react';
import { CalendarPlus, ListTodo, Sparkles } from 'lucide-react';
import { Progress } from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';

const TODO_STAGES = [
  { label: 'Reading email content…', target: 22 },
  { label: 'Analyzing message with AI…', target: 48 },
  { label: 'Extracting todo details…', target: 72 },
  { label: 'Finalizing todo draft…', target: 90 },
] as const;

const APPOINTMENT_STAGES = [
  { label: 'Reading email content…', target: 22 },
  { label: 'Analyzing message with AI…', target: 48 },
  { label: 'Extracting appointment details…', target: 72 },
  { label: 'Finalizing appointment draft…', target: 90 },
] as const;

export interface AiDraftProgressBannerProps {
  draftType: 'todo' | 'appointment';
  className?: string;
}

const AiDraftProgressBanner: React.FC<AiDraftProgressBannerProps> = ({ draftType, className }) => {
  const { colors } = useTheme();
  const [stageIndex, setStageIndex] = useState(0);
  const [percent, setPercent] = useState(4);

  const isTodo = draftType === 'todo';
  const stages = isTodo ? TODO_STAGES : APPOINTMENT_STAGES;
  const title = isTodo ? 'Drafting todo from email' : 'Drafting appointment from email';
  const stage = stages[Math.min(stageIndex, stages.length - 1)];

  useEffect(() => {
    setStageIndex(0);
    setPercent(4);
  }, [draftType]);

  useEffect(() => {
    const tick = window.setInterval(() => {
      setPercent((prev) => {
        const target = stages[Math.min(stageIndex, stages.length - 1)].target;
        if (prev >= target) return prev;
        const step = prev < 40 ? 1.8 : prev < 75 ? 0.9 : 0.35;
        return Math.min(target, prev + step);
      });
    }, 160);
    return () => window.clearInterval(tick);
  }, [stageIndex, stages]);

  useEffect(() => {
    if (stageIndex >= stages.length - 1) return;
    const advance = window.setTimeout(() => {
      setStageIndex((i) => Math.min(i + 1, stages.length - 1));
    }, 4500);
    return () => window.clearTimeout(advance);
  }, [stageIndex, stages.length]);

  return (
    <div
      className={cn(
        'rounded-xl border px-3 py-3 sm:px-4',
        isTodo ? 'border-emerald-500/35 bg-emerald-500/5' : 'border-sky-500/35 bg-sky-500/5',
        colors.border,
        className,
      )}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="flex items-start gap-3">
        <div
          className={cn(
            'mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
            isTodo
              ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
              : 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300',
          )}
        >
          {isTodo ? (
            <ListTodo className="h-4 w-4" aria-hidden />
          ) : (
            <CalendarPlus className="h-4 w-4" aria-hidden />
          )}
        </div>

        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <Sparkles className="h-4 w-4 shrink-0 text-indigo-500 dark:text-indigo-400" aria-hidden />
            <p className={cn('text-sm font-semibold', colors.text)}>{title}</p>
            <span className={cn('text-xs tabular-nums', colors.textMuted)}>{Math.round(percent)}%</span>
          </div>

          <p className={cn('text-xs sm:text-sm', colors.textSecondary)}>{stage.label}</p>

          <Progress value={percent} size="sm" className="w-full" />

          <p className={cn('text-[11px]', colors.textMuted)}>
            AI is working on this email — large messages can take up to a minute.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AiDraftProgressBanner;
